import React, { useEffect, useState } from "react";
import { Alert, Spin, Button } from "antd";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FormAction } from "../../../types/enums";
import GETJwtToken from "../../../redux/authentication/authentication";
import { useLazyGetRoleQuery } from "../../../redux/roles/roleAPI";
import RolesCreateRoleForm from "./RolesCreateRoleForm";

/**
 * Dedicated full-page READ-ONLY route — reached from the Roles table's "View"
 * action on a role owned by the master organization (see RolesTable.tsx).
 * Those roles are LiftNet-level templates: a tenant user must be able to see
 * exactly which permissions one grants without any way to change it, so this
 * renders the same Create/Edit-unified form in FormAction.View — every input
 * disabled, no save button, and the permission cards listing ONLY the grants
 * the role actually holds.
 *
 * The role is fetched by id like RolesEdit does, but falls back to the record
 * the table pushed through router state: GET /roles/ lists master-org roles to
 * every tenant (they inherit from them through base_role_id) while the
 * single-role GET is not guaranteed to be scoped that widely, and the list row
 * already carries the menu this page renders.
 */
export default function RolesView() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id = "" } = useParams<{ id: string }>();
  const normalizedRoleId = decodeURIComponent(String(id ?? "")).trim();
  const roleFromTable = (location.state as { role?: any } | null)?.role ?? null;

  const [roleData, setRoleData] = useState<any>(null);
  const [loadError, setLoadError] = useState<string>("");
  // The form builds its read-only permission cards ONCE on mount, so nothing
  // is rendered until the fetch has settled one way or the other — handing it
  // the table row first and swapping in the fetched role afterwards would
  // leave it showing the menu it mounted with.
  const [isResolved, setIsResolved] = useState(false);

  const [triggerGetRole] = useLazyGetRoleQuery();

  useEffect(() => {
    async function loadRole() {
      if (!normalizedRoleId || normalizedRoleId === "[object Object]") {
        if (roleFromTable) setRoleData(roleFromTable);
        else setLoadError("Invalid role id");
        setIsResolved(true);
        return;
      }

      try {
        const token = await GETJwtToken();
        const response = await triggerGetRole({ roleId: normalizedRoleId, token }).unwrap();
        const resolvedRole = response?.data ?? response;

        if (!resolvedRole) {
          if (roleFromTable) setRoleData(roleFromTable);
          else setLoadError("Role not found");
          setIsResolved(true);
          return;
        }

        setRoleData(resolvedRole);
      } catch {
        // Fall back to the row the table handed over rather than erroring out
        // when the single-role GET is out of the caller's scope.
        if (roleFromTable) setRoleData(roleFromTable);
        else setLoadError("Failed to load role data");
      }
      setIsResolved(true);
    }

    loadRole();
  }, [normalizedRoleId, triggerGetRole]);

  const navigateToRoles = () => navigate("/organization-management/roles");

  if (!isResolved) {
    return <Spin spinning tip="Loading" />;
  }

  if (loadError) {
    return (
      <Alert
        type="error"
        showIcon
        message="View role"
        description={loadError}
        action={
          <Button type="link" onClick={navigateToRoles}>
            Back to roles
          </Button>
        }
      />
    );
  }

  if (!roleData) {
    return null;
  }

  return (
    <RolesCreateRoleForm
      mode={FormAction.View}
      roleId={normalizedRoleId}
      initialRole={roleData}
      onCancel={navigateToRoles}
    />
  );
}
